import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuthStore } from '@/store';
import { API_HOST } from '@/constants/config';

const GAB_VID_KEY = '@gab_vid';
const GAB_VID_COOKIE = 'gab_vid';
const EXPERIMENTS_CACHE_KEY = '@gab_experiments_config';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years

let _visitorId = null;
let _experimentsConfig = null;
let _fetchPromise = null;

function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

function deterministicVariant(visitorId, experimentName, variants) {
    const str = visitorId + ':' + experimentName;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
    }
    const normalized = Math.abs(hash) / 0x7FFFFFFF;

    let cumulative = 0;
    for (const v of variants) {
        cumulative += v.weight;
        if (normalized < cumulative) return v.key;
    }
    return variants[variants.length - 1].key;
}

// -- Visitor ID management --

async function getVisitorId() {
    if (_visitorId) return _visitorId;

    if (Platform.OS === 'web') {
        const match = document.cookie.match(new RegExp(`(?:^|; )${GAB_VID_COOKIE}=([^;]*)`));
        if (match) {
            _visitorId = decodeURIComponent(match[1]);
            return _visitorId;
        }
        _visitorId = generateUUID();
        document.cookie = `${GAB_VID_COOKIE}=${_visitorId}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
        return _visitorId;
    }

    try {
        const stored = await AsyncStorage.getItem(GAB_VID_KEY);
        if (stored) {
            _visitorId = stored;
            return _visitorId;
        }
    } catch {}

    _visitorId = generateUUID();
    try {
        await AsyncStorage.setItem(GAB_VID_KEY, _visitorId);
    } catch {}
    return _visitorId;
}

// -- Experiment config fetching --

async function fetchExperimentsConfig() {
    if (_experimentsConfig) return _experimentsConfig;
    if (_fetchPromise) return _fetchPromise;

    _fetchPromise = (async () => {
        // Try cache first
        try {
            if (Platform.OS === 'web') {
                const cached = sessionStorage.getItem(EXPERIMENTS_CACHE_KEY);
                if (cached) {
                    _experimentsConfig = JSON.parse(cached);
                    return _experimentsConfig;
                }
            } else {
                const cached = await AsyncStorage.getItem(EXPERIMENTS_CACHE_KEY);
                if (cached) {
                    _experimentsConfig = JSON.parse(cached);
                    return _experimentsConfig;
                }
            }
        } catch {}

        // Fetch from API
        try {
            const res = await fetch(`${API_HOST}/json/experiments/public`);
            const data = await res.json();
            if (data?.data?.experiments) {
                _experimentsConfig = data.data.experiments;
                try {
                    const json = JSON.stringify(_experimentsConfig);
                    if (Platform.OS === 'web') {
                        sessionStorage.setItem(EXPERIMENTS_CACHE_KEY, json);
                    } else {
                        await AsyncStorage.setItem(EXPERIMENTS_CACHE_KEY, json);
                    }
                } catch {}
                return _experimentsConfig;
            }
        } catch {}

        _experimentsConfig = [];
        return _experimentsConfig;
    })();

    return _fetchPromise;
}

/**
 * Returns the variant for the given experiment.
 * Logged-in users: reads from user.experiments (server-authoritative).
 * Anonymous visitors: computes deterministically from gab_vid + experiment config.
 */
export function useExperiment(experimentName) {
    const user = useAuthStore(state => state.user);
    const [anonymousVariant, setAnonymousVariant] = useState('control');

    const userVariant = user?.experiments?.[experimentName];

    // console.log(`[useExperiment] "${experimentName}" → userVariant=${userVariant}, hasUser=${!!user}, allExperiments=${JSON.stringify(user?.experiments)}`);

    useEffect(() => {
        if (userVariant) return;

        let cancelled = false;

        (async () => {
            const [vid, config] = await Promise.all([
                getVisitorId(),
                fetchExperimentsConfig(),
            ]);

            if (cancelled) return;

            const exp = config.find(e => e.name === experimentName);
            if (!exp?.variants?.length) {
                // console.log(`[useExperiment] "${experimentName}" → anonymous fallback, no config found`);
                return;
            }

            const variant = deterministicVariant(vid, experimentName, exp.variants);
            // console.log(`[useExperiment] "${experimentName}" → anonymous variant="${variant}" (vid=${vid?.slice(0, 8)}...)`);
            setAnonymousVariant(variant);
        })();

        return () => { cancelled = true; };
    }, [experimentName, userVariant]);

    const result = userVariant || anonymousVariant;
    // console.log(`[useExperiment] "${experimentName}" → returning "${result}"`);
    return result;
}

export { getVisitorId, fetchExperimentsConfig, deterministicVariant };
